import classNames from 'classnames';
import { useFormik } from 'formik';
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { object, string } from 'yup';
import theRapixLOgo from '../assets/Logo_main.svg';
import theRapixLOgoSmall from '../assets/Logo_small.svg';
import therapixDarkLogo from '../assets/therapix-03.svg';
import FieldWrap from '../components/form/FieldWrap';
import Input from '../components/form/Input';
import Validation from '../components/form/Validation';
import Icon from '../components/icon/Icon';
import PageWrapper from '../components/layouts/PageWrapper/PageWrapper';
import Button from '../components/ui/Button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../components/ui/Modal';
import { appPages, authPages } from '../config/pages.config';
import { useAuth } from '../context/authContext';
import { loginUser, resendVerificationEmail } from '../services/apiService';
import LogoTemplate from '../templates/layouts/Logo/Logo.template';

type TValues = {
	email: string;
	password: string;
	islogout?: string;
};

const validationSchema = object({
	email: string().email('Invalid email address').required('Required'),
	password: string().min(6, 'Password must be at least 6 characters').required('Required'),
});

const LoginPage = () => {
	const { onLogin, onLogout } = useAuth();
	const [showModal, setShowModal] = useState(false);
	const [showResendVeriModal, setShowResendVeriModal] = useState(false);
	const { loading, call } = loginUser();
	const { loadingRes, resendVefEmail } = resendVerificationEmail();
	const { planData } = useAuth();
	const [islogout, setIslogout] = useState('');
	const [passwordShowStatus, setPasswordShowStatus] = useState<boolean>(false);
	const gradientRef = useRef<HTMLDivElement>(null);
	const isHoveringRef = useRef(false);
	const animationFrameRef = useRef<number | null>(null);
	const navigate = useNavigate();

	useEffect(() => {
		let targetX = 50;
		let targetY = 50;
		let currentX = 50;
		let currentY = 50;
		let lastUpdateTime = 0;
		let velocityX = 0.01;
		let velocityY = 0.015;
		
		const updateGradientPosition = (timestamp: number) => {
			if (!gradientRef.current) return;
			
			const delta = timestamp - lastUpdateTime;
			lastUpdateTime = timestamp;
			
			// Auto movement when not hovering
			if (!isHoveringRef.current) {
				// Continuous movement with smooth bounce
				currentX += velocityX * delta * 0.5;
				currentY += velocityY * delta * 0.5;
				
				// Bounce when reaching edges
				if (currentX <= 25 || currentX >= 75) {
					velocityX *= -1;
				}
				if (currentY <= 25 || currentY >= 75) {
					velocityY *= -1;
				}
				
				// Ensure within boundaries
				currentX = Math.max(25, Math.min(75, currentX));
				currentY = Math.max(25, Math.min(75, currentY));
			}
			
			// Apply the gradient with current position and smaller radius
			gradientRef.current.style.background = 
				`radial-gradient(circle at ${currentX}% ${currentY}%, #557C93 0%, #08203E 55%)`;
			
			animationFrameRef.current = requestAnimationFrame(updateGradientPosition);
		};
		
		const handleMouseMove = (e: MouseEvent) => {
			if (!gradientRef.current) return;
			
			const rect = gradientRef.current.getBoundingClientRect();
			const x = ((e.clientX - rect.left) / rect.width) * 100;
			const y = ((e.clientY - rect.top) / rect.height) * 100;
			
			// Update current position directly when mouse is moving
			currentX = x;
			currentY = y;
		};
		
		const handleMouseEnter = () => {
			isHoveringRef.current = true;
		};
		
		const handleMouseLeave = () => {
			isHoveringRef.current = false;
		};
		
		const gradient = gradientRef.current;
		if (gradient) {
			gradient.addEventListener('mousemove', handleMouseMove);
			gradient.addEventListener('mouseenter', handleMouseEnter);
			gradient.addEventListener('mouseleave', handleMouseLeave);
			
			// Start animation loop
			animationFrameRef.current = requestAnimationFrame(updateGradientPosition);
		}
		
		return () => {
			if (gradient) {
				gradient.removeEventListener('mousemove', handleMouseMove);
				gradient.removeEventListener('mouseenter', handleMouseEnter);
				gradient.removeEventListener('mouseleave', handleMouseLeave);
			}
			
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, []);

	const formik = useFormik({
		initialValues: {
			email: '',
			password: '',
		},
		validationSchema,
		onSubmit: (values: TValues, { setFieldError }) => {
			call(values.email, values.password, islogout).subscribe({
				next: (res: any) => {
					onLogin(res.data.token);
				},
				error: (e: any) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}
					if (e?.status === 403) {
						setShowResendVeriModal(true);
						return;
					}
					if (e?.status === 461) {
						setShowModal(true);
						return;
					}
					toast.error(e?.data?.msg ?? e.message);
				},
			});
		},
	});

	function resendVerificationMail() {
		resendVefEmail("ketpet1@yopmail.com").subscribe({
			next: (res: any) => {
				setShowResendVeriModal(false);
				toast.success(res.msg);
			},
			error: (e: any) => {
				if (e?.status === 409) {
					onLogout(e?.status);
				}
				if (e?.status === 403) {
					setShowResendVeriModal(true);
					return;
				}
				if (e?.status === 461) {
					setShowModal(true);
					return;
				}
			},
		});
	}

	return (
		<PageWrapper
			isBeforeLoginRoute={true}
			isProtectedRoute={false}
			className='bg-white'
			name='Sign In'>
			<div className='flex h-full items-center justify-center'>
				<div className='flex h-full w-full flex-col md:flex-row'>
					{/* Gradient section - hidden on mobile, visible from md breakpoint */}
					<div ref={gradientRef} className='hidden md:flex md:w-1/2 interactive-gradient'>
						<div className='mx-auto my-5 flex basis-2/3 relative z-10'>
							<img src={therapixDarkLogo} alt='THERapix' className='home-logo' />
						</div>
					</div>
					{/* Form section - full width on mobile, half width on desktop */}
					<div className='w-full md:w-1/2 flex items-center justify-center text-black px-4 py-6 md:px-0 md:py-0 flex-center-mobile min-h-screen'>
						<div className='flex w-full max-w-sm flex-col gap-6 md:gap-8 items-center'>
							<div className='flex flex-col items-center md:items-start md:flex-row md:gap-4 w-full'>
								<div className='logo-small mb-4 md:mb-0 flex justify-center md:justify-start w-full md:w-auto'>
									<img src={theRapixLOgoSmall} alt='THERapix' className='home-logo' />
								</div>
								<div className='flex flex-col items-center md:items-start gap-2 md:gap-4 w-full'>
									<span className='text-3xl md:text-4xl font-semibold text-center md:text-left text-center-mobile'>Sign in</span>
									<span className='text-center md:text-left text-center-mobile'>Sign in with email address</span>
								</div>
							</div>
							
							<form
								className='flex w-full flex-col gap-4 items-center form-field-wrapper'
								noValidate>
								<div
									className={classNames('w-full', {
										'mb-2': !formik.isValid,
									})}>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.email}
										invalidFeedback={formik.errors.email}>
										<FieldWrap
											firstSuffix={
												<Icon icon='HeroEnvelope' className='mx-2' />
											}>
											<Input
												dimension='default'
												id='email'
												autoComplete='email'
												name='email'
												placeholder='Email'
												value={formik.values.email}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												className="w-full"
											/>
										</FieldWrap>
									</Validation>
								</div>
								
								<div
									className={classNames('w-full', {
										'mb-2': !formik.isValid,
									})}>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.password}
										invalidFeedback={formik.errors.password}>
										<FieldWrap
											firstSuffix={<Icon icon='HeroKey' className='mx-2' />}
											lastSuffix={
												<Icon
													className='mx-2 cursor-pointer'
													icon={
														passwordShowStatus
															? 'HeroEyeSlash'
															: 'HeroEye'
													}
													onClick={() => {
														setPasswordShowStatus(!passwordShowStatus);
													}}
												/>
											}>
											<Input
												dimension='default'
												type={passwordShowStatus ? 'text' : 'password'}
												autoComplete='current-password'
												id='password'
												name='password'
												placeholder='Password'
												value={formik.values.password}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												className="w-full"
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														formik.handleSubmit();
													}
												}}
											/>
										</FieldWrap>
									</Validation>
								</div>
								<div className='w-full flex-center-mobile flex-col'>
									<Button
										isLoading={loading}
										size='lg'
										variant='solid'
										className='primary-bg w-full font-semibold'
										onClick={() => formik.handleSubmit()}>
										Sign in
									</Button>
									<div className='w-full text-center mt-4'>
										<Link
											to={`${authPages.forgotPasswordPage.to}`}
											className='text-md font-semibold hover:text-inherit'>
											Forgot password
										</Link>
									</div>
								</div>
							</form>
							
							<div className='w-full text-center flex-center-mobile'>
								<span className='flex justify-center gap-2 text-sm flex-wrap'>
									<span className='flex items-center text-zinc-400 text-center-mobile'>
										Don't have an account?
									</span>
									<Link
										to={`${authPages.signupPage.to}`}
										className='text-md font-semibold hover:text-inherit'>
										Sign up
									</Link>
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Modal isOpen={showModal} setIsOpen={setShowModal} isCentered size='sm'>
				<ModalHeader setIsOpen={setShowModal}>Confirm Logout </ModalHeader>
				<ModalBody>
					<>
						<p className='mb-0 text-lg'>
							Are you sure you want to logout from another device ?
						</p>
					</>
				</ModalBody>
				<ModalFooter className={`justify-end gap-3 pt-3`}>
					<>
						<Button
							variant='solid'
							color='gray'
							colorIntensity='500'
							className='cursor-pointer text-white'
							onClick={() => setShowModal(false)}>
							Cancel
						</Button>
						<Button
							isLoading={loading}
							variant='solid'
							color='red'
							colorIntensity='600'
							className='cursor-pointer text-white'
							onClick={() => {
								setIslogout('logout');
								formik.handleSubmit();
							}}>
							Logout
						</Button>
					</>
				</ModalFooter>
			</Modal>
			<Modal isOpen={showResendVeriModal} setIsOpen={setShowResendVeriModal} isCentered size='sm'>
				<ModalHeader setIsOpen={setShowResendVeriModal}>Resend verification link</ModalHeader>
				<ModalBody>
					<>
						<p className='mb-0 text-lg'>
							Email is not verified. Please verify your email address from verification link.
						</p>
					</>
				</ModalBody>
				<ModalFooter className={`justify-end gap-3 pt-3`}>
					<>
						<Button
							variant='solid'
							color='gray'
							colorIntensity='500'
							className='cursor-pointer text-white'
							onClick={() => setShowResendVeriModal(false)}>
							Cancel
						</Button>
						<Button
							isLoading={loadingRes}
							variant='solid'
							style={{backgroundColor : '#08203e', border: "2px solid #08203e"}}
							colorIntensity='600'
							className='cursor-pointer text-white'
							onClick={() => {
								resendVerificationMail();
							}}>
							Resend Email
						</Button>
					</>
				</ModalFooter>
			</Modal>
		</PageWrapper>
	);
};

export default LoginPage;
